/** Accessible rendering of the Rust-generated weekly personal energy map.
 * @module apps/web/src/components/settings/EnergyHeatmap
 */
import { useState } from 'react';

import type { EnergyHeatmapCell as EnergyCell, EnergyHeatmap as EnergyHeatmapData } from '@/lib/energyModel';

/** Axis names displayed in the map's selector and accessible descriptions. */
const AXES = {
  /** Energy available for productive work, on the zero-to-ten axis. */
  productivity: 'Productivity',
  /** Energy available for rejuvenating activities, on the zero-to-ten axis. */
  rejuvenation: 'Rejuvenation',
  /** Predicted resistance to activity, on the zero-to-ten axis. */
  resistance: 'Resistance',
};
/** Sunday-first column headings matching the server contract. */
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** Read confidence for the selected axis, retaining compatibility with older aggregate-only responses. */
function cellConfidence(cell: EnergyCell, axis: keyof typeof AXES): number {
  return cell[`${axis}Confidence`] ?? cell.confidence;
}

/** Display all weekly hours with explicit unknown cells and text equivalents for color. */
export function EnergyHeatmap({
  heatmap,
}: {
  /** Precomputed map from the personal model status endpoint. */
  heatmap?: EnergyHeatmapData | null;
}) {
  const [axis, setAxis] = useState<keyof typeof AXES>('productivity');
  const [selected, setSelected] = useState<EnergyCell | null>(null);
  return (
    <section className="space-y-3 border-t pt-4" aria-labelledby="energy-patterns-title">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 id="energy-patterns-title" className="font-medium">
          Your weekly energy patterns
        </h3>
        <label className="flex items-center gap-2 text-sm">
          <span className="sr-only">Energy axis</span>
          <select
            className="bg-background rounded-md border px-2 py-1"
            value={axis}
            onChange={event => setAxis(event.target.value as keyof typeof AXES)}
          >
            {Object.entries(AXES).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </label>
      </div>
      {!heatmap ? (
        <p className="text-muted-foreground text-sm">
          Your energy map will appear here when your check-in profile is available.
        </p>
      ) : (
        <>
          <p className="text-muted-foreground text-xs">
            Local times in {heatmap.timezone}. Predictions use your personal check-ins and available
            model evidence.
          </p>
          <div className="max-h-96 overflow-auto rounded-md border">
            <table className="w-full table-fixed border-separate border-spacing-1 text-xs">
              <caption className="sr-only">
                {AXES[axis]} by weekday and hour, from zero to ten. A dash means there is no
                evidence yet.
              </caption>
              <thead>
                <tr>
                  <th scope="col" className="w-12">
                    Hour
                  </th>
                  {DAYS.map(day => (
                    <th key={day} scope="col" aria-label={day}>
                      {day.slice(0, 2)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: 24 }, (_, hour) => (
                  <tr key={hour}>
                    <th scope="row" className="text-muted-foreground font-normal">
                      {String(hour).padStart(2, '0')}:00
                    </th>
                    {DAYS.map((day, weekday) => {
                      const cell = heatmap.cells.find(
                        value => value.weekday === weekday && value.hour === hour
                      );
                      const known = cell && cellConfidence(cell, axis) > 0 && Number.isFinite(cell[axis]);
                      const label = known
                        ? `${day} ${hour}:00, ${AXES[axis]} ${cell[axis].toFixed(1)} of 10, confidence ${Math.round(cellConfidence(cell, axis) * 100)}%`
                        : `${day} ${hour}:00, no evidence yet`;
                      return (
                        <td key={day}>
                          <button
                            type="button"
                            className="focus-visible:ring-ring h-7 w-full rounded-sm border border-transparent text-xs focus-visible:outline-none focus-visible:ring-2"
                            style={
                              known
                                ? {
                                    backgroundColor: `hsl(165 55% ${94 - Math.min(10, Math.max(0, cell[axis])) * 6}%)`,
                                    color: '#000000',
                                  }
                                : { backgroundColor: 'var(--muted)' }
                            }
                            aria-label={label}
                            title={label}
                            onFocus={() => setSelected(cell ?? null)}
                            onClick={() => setSelected(cell ?? null)}
                          >
                            {known ? cell[axis].toFixed(1) : '—'}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="text-muted-foreground flex flex-wrap items-center gap-2 text-xs">
            <span>Low 0</span>
            <span
              aria-hidden="true"
              className="h-2 w-20 rounded"
              style={{
                background: 'linear-gradient(to right, hsl(165 55% 94%), hsl(165 55% 34%))',
              }}
            />
            <span>10 High</span>
            <span>· — No evidence</span>
          </div>
          <p className="text-muted-foreground min-h-8 text-xs" aria-live="polite">
            {selected && cellConfidence(selected, axis) > 0
              ? `${DAYS[selected.weekday]} ${String(selected.hour).padStart(2, '0')}:00 · ${AXES[axis]} ${selected[axis].toFixed(1)}/10 · ${Math.round(cellConfidence(selected, axis) * 100)}% confidence`
              : 'Select an hour to inspect its prediction and confidence. More check-ins help reveal your patterns.'}
          </p>
        </>
      )}
    </section>
  );
}
